import { Box, ClickAwayListener, Popper, TextField, Typography } from '@material-ui/core'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import { BlockPicker, ColorResult } from 'react-color'
import { Colors, Toaster } from 'src/components/atoms'
import { SelectBox } from 'src/components/atoms/SelectBox'
import { TextAlignSelector, TextAlign } from 'src/components/molecules/TextAlignSelector'
import { FontSizeInput } from 'src/components/molecules/FontSizeInput'

export type FontFamily = 'gothic' | 'mincho' | 'monospace'

export interface TextStyleMenuProps {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  textAlign: TextAlign
  fontFamily: FontFamily
  fontSize: number
  textColor: string
  onTextAlignChange: (value: TextAlign) => void
  onFontFamilyChange: (value: FontFamily) => void
  onFontSizeChange: (value: number) => void
  onTextColorChange: (value: string) => void
  disabled?: boolean
}

export const FONT_OPTIONS = [
  { value: 'gothic', label: 'ゴシック体' },
  { value: 'mincho', label: '明朝体' },
  { value: 'monospace', label: '等幅フォント' }
]

export const FONT_FAMILY_CSS: Record<FontFamily, string> = {
  gothic: '"Hiragino Kaku Gothic ProN", "Noto Sans JP", sans-serif',
  mincho: '"Hiragino Mincho ProN", "Noto Serif JP", serif',
  monospace: '"SF Mono", "Noto Sans Mono", monospace'
}

// テキスト用カラーリスト（12色: ベースカラー + アクセントカラー）
const TEXT_COLOR_LIST = [
  // ベースカラー（4色）
  '#333333', // デフォルト黒
  '#666666', // ダークグレー
  Colors.base.middleGray, // ミドルグレー
  '#999999', // ライトグレー
  // アクセントカラー（8色）
  Colors.accent.keyPurple.default, // パープル
  Colors.accent.keyBlue.default, // ブルー
  Colors.option.blue, // シアン
  Colors.option.green, // グリーン
  Colors.accent.keyYellow.default, // イエロー
  Colors.option.orange, // オレンジ
  Colors.accent.keyRed.default, // レッド
  Colors.option.pink // ピンク
]

const useStyles = makeStyles(() =>
  createStyles({
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1300
    },
    draggableContainer: {
      position: 'absolute',
      zIndex: 1301
    },
    paper: {
      padding: '16px',
      paddingTop: '28px',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      border: `1px solid ${Colors.background.gray}`,
      background: '#fff',
      minWidth: '650px'
    },
    dragHandle: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '24px',
      background: Colors.background.lightGray,
      borderTopLeftRadius: '8px',
      borderTopRightRadius: '8px',
      cursor: 'move',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderBottom: `1px solid ${Colors.background.gray}`,
      '&:hover': {
        background: Colors.background.gray
      }
    },
    dragHandleIcon: {
      width: '32px',
      height: '4px',
      borderRadius: '2px',
      background: Colors.base.middleGray
    },
    content: {
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start'
    },
    menuItem: {
      flex: 1,
      minWidth: 0
    },
    label: {
      fontSize: '11px',
      fontWeight: 600,
      color: Colors.base.middleGray,
      marginBottom: '6px',
      display: 'block',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    fontSelectWrapper: {
      width: '100%'
    },
    fontSizeWrapper: {
      width: '100%'
    },
    colorInputWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    colorBox: {
      width: '32px',
      height: '32px',
      borderRadius: '4px',
      border: `1px solid ${Colors.background.gray}`,
      cursor: 'pointer',
      transition: 'box-shadow 0.2s ease',
      flexShrink: 0,
      '&:hover': {
        boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
      }
    },
    colorInput: {
      width: '80px',
      '& .MuiOutlinedInput-input': {
        padding: '8px',
        fontSize: '13px',
        fontFamily: 'monospace'
      }
    },
    colorPickerPopper: {
      zIndex: 1400
    },
    colorPickerContainer: {
      background: '#fff',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      padding: '8px',
      '& .block-picker': {
        boxShadow: 'none !important'
      }
    }
  })
)

export const TextStyleMenu = ({
  open,
  anchorEl,
  onClose,
  textAlign,
  fontFamily,
  fontSize,
  textColor,
  onTextAlignChange,
  onFontFamilyChange,
  onFontSizeChange,
  onTextColorChange,
  disabled = false
}: TextStyleMenuProps) => {
  const classes = useStyles()
  const nodeRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [initialPosition, setInitialPosition] = useState<{ x: number; y: number } | null>(null)
  const [colorPickerAnchorEl, setColorPickerAnchorEl] = useState<HTMLElement | null>(null)
  const [colorInputValue, setColorInputValue] = useState(textColor)
  const colorPickerOpen = Boolean(colorPickerAnchorEl)

  // textColorが外部から変更された場合に同期
  useEffect(() => {
    setColorInputValue(textColor)
  }, [textColor])

  const handleColorBoxClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setColorPickerAnchorEl(event.currentTarget)
  }, [])

  const handleColorPickerClose = useCallback(() => {
    setColorPickerAnchorEl(null)
  }, [])

  const handleColorChangeComplete = useCallback(
    (color: ColorResult) => {
      // 6桁のHEXカラーコードのみ適用（3桁や不正な値は無視）
      const hexPattern = /^#[A-Fa-f0-9]{6}$/
      if (hexPattern.test(color.hex)) {
        onTextColorChange(color.hex)
      }
      // パレット内のinput入力時はパレットを閉じない（パレット外クリックで閉じる）
    },
    [onTextColorChange]
  )

  const handleColorInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setColorInputValue(value)
  }, [])

  const handleColorInputBlur = useCallback(() => {
    // 有効なHEXカラーコードかチェック
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
    if (hexPattern.test(colorInputValue)) {
      onTextColorChange(colorInputValue)
    } else {
      // 無効な場合はエラー表示して元の値に戻す
      Toaster.error('正しいカラーコード（例：#FF5722）を入力してください')
      setColorInputValue(textColor)
    }
  }, [colorInputValue, onTextColorChange, textColor])

  const handleColorInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleColorInputBlur()
      }
    },
    [handleColorInputBlur]
  )

  // anchorElの位置から初期位置を計算
  useEffect(() => {
    if (open && anchorEl) {
      const rect = anchorEl.getBoundingClientRect()
      setInitialPosition({
        x: rect.left,
        y: rect.bottom + 8
      })
      setPosition({ x: 0, y: 0 })
    } else if (!open) {
      setInitialPosition(null)
    }
  }, [open, anchorEl])

  // ドラッグ終了時の位置を保存
  const handleDragStop = useCallback((_e: DraggableEvent, data: DraggableData) => {
    setPosition({ x: data.x, y: data.y })
  }, [])

  // オーバーレイクリックで閉じる
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose()
      }
    },
    [onClose]
  )

  if (!open || !initialPosition) {
    return null
  }

  return (
    <Box className={classes.overlay} onClick={handleOverlayClick}>
      <Draggable nodeRef={nodeRef} handle={`.${classes.dragHandle}`} position={position} onStop={handleDragStop}>
        <div
          ref={nodeRef}
          className={classes.draggableContainer}
          style={{
            left: initialPosition.x,
            top: initialPosition.y
          }}
        >
          <Box className={classes.paper}>
            <Box className={classes.dragHandle}>
              <Box className={classes.dragHandleIcon} />
            </Box>
            <Box className={classes.content}>
              <Box className={classes.menuItem}>
                <Typography className={classes.label}>配置</Typography>
                <TextAlignSelector value={textAlign} onChange={onTextAlignChange} disabled={disabled} />
              </Box>

              <Box className={classes.menuItem}>
                <Typography className={classes.label}>フォント</Typography>
                <Box className={classes.fontSelectWrapper}>
                  <SelectBox
                    name="fontFamily"
                    value={fontFamily}
                    optionList={FONT_OPTIONS}
                    onChange={e => onFontFamilyChange(e.target.value as FontFamily)}
                    size="medium"
                    disabled={disabled}
                  />
                </Box>
              </Box>

              <Box className={classes.menuItem}>
                <Typography className={classes.label}>サイズ（px）</Typography>
                <Box className={classes.fontSizeWrapper}>
                  <FontSizeInput value={fontSize} onChange={onFontSizeChange} disabled={disabled} />
                </Box>
              </Box>

              <Box className={classes.menuItem}>
                <Typography className={classes.label}>色</Typography>
                <Box className={classes.colorInputWrapper}>
                  <Box
                    className={classes.colorBox}
                    style={{ backgroundColor: textColor }}
                    onClick={handleColorBoxClick}
                  />
                  <TextField
                    className={classes.colorInput}
                    variant="outlined"
                    size="small"
                    value={colorInputValue}
                    onChange={handleColorInputChange}
                    onBlur={handleColorInputBlur}
                    onKeyDown={handleColorInputKeyDown}
                    disabled={disabled}
                    placeholder="#333333"
                  />
                </Box>
                <Popper
                  open={colorPickerOpen}
                  anchorEl={colorPickerAnchorEl}
                  placement="bottom-start"
                  className={classes.colorPickerPopper}
                >
                  <ClickAwayListener onClickAway={handleColorPickerClose}>
                    <Box className={classes.colorPickerContainer}>
                      <BlockPicker
                        color={textColor}
                        onChangeComplete={handleColorChangeComplete}
                        colors={TEXT_COLOR_LIST}
                        triangle="hide"
                        width="146px"
                      />
                    </Box>
                  </ClickAwayListener>
                </Popper>
              </Box>
            </Box>
          </Box>
        </div>
      </Draggable>
    </Box>
  )
}
